import { useContext, useMemo } from 'react'
import { UserPermissionsContext } from 'app/hooks/useUserPermission'
import type { Permission } from 'app/types/entities'
import { useUser } from 'app/utils'

/**
 * Hook to get a permission checker function for the current user.
 * Uses context internally - no need to pass user or permissions.
 *
 * @returns Function that checks if user has a specific permission
 *
 * @example
 * const checkPermission = useCheckPermission()
 * const canManageUsers = checkPermission('users:manage')
 * const canEditContact = checkPermission(['org:contacts:edit-channel', 'contacts:attributions'])
 */
export function useCheckPermission(): (permission: Permission | Permission[]) => boolean {
  const { permissions } = useContext(UserPermissionsContext)
  const user = useUser()

  return useMemo(() => {
    return (permission: Permission | Permission[]) => {
      // undefined = not loaded or error state = no permissions
      if (!user) {
        return false
      }

      // Super users have all permissions
      if (user.type === `super`) {
        return true
      }

      // undefined = not loaded or error state = no permissions
      if (permissions === undefined) {
        return false
      }

      // If array of permissions, return true if ANY match
      if (Array.isArray(permission)) {
        return permission.some((p) => permissions.includes(p))
      }

      // Single permission check
      return permissions.includes(permission)
    }
  }, [permissions, user?.type])
}
